/**
 * useGraphData Hook
 * Fetches and transforms graph data for visualization
 */

import { useState, useEffect, useCallback, useRef } from 'react';

import { useGraphApi } from '@/shared/services/graphApi';

import {
    transformGraphData,
    calculateConnectionCounts,
} from '../utils/graphHelpers';

// Stable empty object reference to prevent infinite re-renders
const EMPTY_FILTERS = {};

/**
 * Hook for fetching full ecosystem graph data
 * @param {Object} filters - Optional filters { sector, region, programmingTypes }
 * @returns {Object} { graphData, loading, error, refetch }
 */
export function useGraphData(filters) {
    // Use stable reference for empty filters
    const stableFilters = filters || EMPTY_FILTERS;
    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [rawData, setRawData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fetchTrigger, setFetchTrigger] = useState(0);

    const graphApi = useGraphApi();
    const graphApiRef = useRef(graphApi);
    const filtersRef = useRef(stableFilters);

    // Keep refs updated
    graphApiRef.current = graphApi;
    filtersRef.current = stableFilters;

    // Fetch on mount and when filters change (by value, not reference)
    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await graphApiRef.current.fetchFullGraph(
                    filtersRef.current
                );
                if (!cancelled) {
                    setRawData(data);
                    const connectionCounts = calculateConnectionCounts(
                        data.edges
                    );
                    const transformed = transformGraphData(
                        data,
                        connectionCounts
                    );
                    setGraphData(transformed);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching graph data:', err);
                    setError(
                        err.response?.data?.message ||
                            'Failed to load ecosystem graph'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [
        fetchTrigger,
        stableFilters.sector,
        stableFilters.region,
        stableFilters.programmingTypes,
        stableFilters.hasWebsite,
        stableFilters.hasDescription,
    ]);

    const refetch = useCallback(() => {
        setFetchTrigger((t) => t + 1);
    }, []);

    return {
        graphData,
        rawData,
        loading,
        error,
        refetch,
    };
}

/**
 * Hook for fetching organization subgraph data
 * @param {string} organizationId - Organization ID
 * @param {number} depth - Hop depth (1 or 2)
 * @returns {Object} { graphData, loading, error, refetch }
 */
export function useSubgraphData(organizationId, depth = 1) {
    const [graphData, setGraphData] = useState({ nodes: [], links: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fetchTrigger, setFetchTrigger] = useState(0);

    const graphApi = useGraphApi();
    const graphApiRef = useRef(graphApi);
    graphApiRef.current = graphApi;

    useEffect(() => {
        if (!organizationId) {
            setGraphData({ nodes: [], links: [] });
            setLoading(false);
            return;
        }

        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const data = await graphApiRef.current.fetchSubgraph(
                    organizationId,
                    depth
                );
                if (!cancelled) {
                    const connectionCounts = calculateConnectionCounts(
                        data.edges
                    );
                    const transformed = transformGraphData(
                        data,
                        connectionCounts
                    );
                    setGraphData(transformed);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching subgraph data:', err);
                    setError(
                        err.response?.data?.message ||
                            'Failed to load organization connections'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [organizationId, depth, fetchTrigger]);

    const refetch = useCallback(() => {
        setFetchTrigger((t) => t + 1);
    }, []);

    return {
        graphData,
        loading,
        error,
        refetch,
    };
}

/**
 * Hook for fetching graph statistics
 * @returns {Object} { stats, loading, error }
 */
export function useGraphStats() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const graphApi = useGraphApi();
    const graphApiRef = useRef(graphApi);
    graphApiRef.current = graphApi;

    useEffect(() => {
        let cancelled = false;

        const fetchStats = async () => {
            try {
                const data = await graphApiRef.current.fetchStats();
                if (!cancelled) {
                    setStats(data);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching graph stats:', err);
                    setError(
                        err.response?.data?.message ||
                            'Failed to load network statistics'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchStats();

        return () => {
            cancelled = true;
        };
    }, []);

    return { stats, loading, error };
}

/**
 * Hook for fetching most connected organizations
 * @param {number} limit - Number of results
 * @returns {Object} { organizations, loading, error }
 */
export function useMostConnected(limit = 10) {
    const [organizations, setOrganizations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const graphApi = useGraphApi();
    const graphApiRef = useRef(graphApi);
    graphApiRef.current = graphApi;

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            try {
                const data =
                    await graphApiRef.current.fetchMostConnected(limit);
                if (!cancelled) {
                    setOrganizations(data.organizations || []);
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('Error fetching most connected:', err);
                    setError(
                        err.response?.data?.message ||
                            'Failed to load most connected organizations'
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            cancelled = true;
        };
    }, [limit]);

    return { organizations, loading, error };
}

export default useGraphData;
